import { useEffect, useState } from 'react';
import { Button } from 'antd';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';

import { colors } from 'theme/variables';
import { DeliveryDate, FormDeliveryDate } from 'features/benefit/models/benefitModels';
import { getEmptyDeliveryDate } from 'features/benefit/benefitHelpers';
import { IdValueObject } from 'common/models/defaultsModel';
import { InputLabelWithTooltip } from 'common/components/form/components/InputLabelWithTooltip';

import { InterimDate } from '.';
import { Antd3FormProps } from '../antd3';
import { InterimLink } from './InterimLink';
import { getEmptyLink } from 'features/organisation/helpers';

const DynamicFieldSetWrapper = styled.div`
    margin-bottom: 20px;
`;

const FieldSetElementWrapper = styled.div`
    display: flex;
    align-items: center;
    margin-top: 8px;
    gap: 8px;
`;

const RemoveButton = styled(MinusCircleOutlined)`
    color: #b0bac6;
    font-size: 18px;
    cursor: pointer;
    transition: color 0.15s;
    flex-shrink: 0;
    margin-bottom: 20px;
    margin-left: 8px;

    &:hover {
        color: ${colors.red};
    }
`;

const AddButton = styled(Button)`
    &&& {
        margin-top: 8px;
        border-style: dashed;
        border-color: #dce1e8;
        color: #6b7a8d;
        font-family: 'Inter', sans-serif;
        display: flex;
        align-items: center;
        gap: 6px;
        height: 40px;
        border-radius: 8px;

        &:hover {
            border-color: ${colors.primary};
            color: ${colors.primary};
        }
    }
`;

interface DynamicFieldSetProps {
    fieldSetId: string;
    fieldType: DynamicFieldSetFieldType;
    initialFields?: DynamicFieldSetField[] | null;
    label: string;
    tooltip?: string;
    orderByObjectsIndexes?: boolean;
    onFieldsChanged?: (value: Record<string, DynamicFieldSetField>) => void;
}

export interface DynamicFieldSetItemProps {
    fullId: string;
}

export type DynamicFieldSetField = FormDeliveryDate | IdValueObject;
export type DynamicFieldSetFieldType = 'deliveryDate' | 'textInput';

export type Props = DynamicFieldSetProps & Antd3FormProps;

export const DynamicFieldSet = (props: Props) => {
    const [fields, setFields] = useState<Record<string, DynamicFieldSetField>>(
        props.initialFields?.reduce(
            (acc, field) => ({
                ...acc,
                [field.id || uuid()]: field,
            }),
            {},
        ) || ({} as any),
    );

    useEffect(() => {
        props.form.setFieldsValue({ [props.fieldSetId]: fields });

        if (props.onFieldsChanged) {
            props.onFieldsChanged(fields);
        }
    }, [fields]);

    const addFormItem = () => {
        const newField: DynamicFieldSetField =
            props.fieldType === 'deliveryDate' ? getEmptyDeliveryDate() : getEmptyLink();

        setFields({
            ...fields,
            [uuid()]: newField,
        });
    };

    const updateFormItemInput = (
        fieldId: string,
        inputName: string,
        value: string | number | Date,
    ): void => {
        setFields({
            ...fields,
            [fieldId]: {
                ...fields[fieldId],
                [inputName]: value,
            },
        });
    };

    const removeFormItem = (id: string) => {
        const updatedFields = fields;
        delete updatedFields[id];
        setFields({ ...updatedFields });
    };

    const getFieldComponent = (id: string): JSX.Element | null => {
        const field = fields[id];
        const fieldId = field.id || id;

        const fieldSetProps = {
            form: props.form,
            id: fieldId,
            fullId: `${props.fieldSetId}[${fieldId}]`,
            isTemporaryId: !field.id,
            onInputChange: updateFormItemInput,
        };

        if (props.fieldType === 'textInput') {
            return <InterimLink {...(field as { id: string; url: string })} {...fieldSetProps} />;
        }

        return <InterimDate {...(field as DeliveryDate)} {...fieldSetProps} />;
    };

    return (
        <DynamicFieldSetWrapper>
            <InputLabelWithTooltip label={props.label} tooltip={props.tooltip} />
            {Object.keys(fields).map((fieldId) => (
                <FieldSetElementWrapper key={fieldId}>
                    {getFieldComponent(fieldId)}
                    <RemoveButton onClick={() => removeFormItem(fieldId)} />
                </FieldSetElementWrapper>
            ))}
            <AddButton onClick={addFormItem}>
                <PlusOutlined />
                {`Add ${props.placeholder}`}
            </AddButton>
        </DynamicFieldSetWrapper>
    );
};
